<template>
  <div ref="container" class="text-gray-800">
    <!-- Graph 1 (Arrow Plot) -->
    <div v-if="id === 1" class="sm:shadow-md sm:rounded-lg">
      <div class="font-medium bg-gray-100 p-4 sm:p-6 sm:rounded-t-lg">
        Porcentaje de personas diagnosticadas de cáncer con malestar psicológico
        significativo, en abril y diciembre, por sexo, edad, ingresos familiares
        y empeoramiento de su situación económica.
      </div>
      <div class="flex flex-col justify-center items-center p-4 sm:p-6">
        <arrow-plot
          :data="data1.Total"
          :legendLabels="legendLabels"
          :xAxisDomain="[0, 80]"
        />
        <ButtonSwitch
          v-if="width > 700"
          v-model="segment"
          :options="segments"
          class="bg-gray-100 p-1 shadow-inner text-xs rounded-full my-4"
          item-class="text-gray-600 rounded-full hover:text-gray-700 focus:text-gray-700 transition font-medium py-2 px-4 bg-transparent focus:outline-none truncate sm:overflow-visible w-24 sm:w-auto"
          item-active-class="text-primary-700 hover:text-primary-700 focus:text-primary-800 ring-transparent ring-0"
        />
        <select v-else v-model="segment" class="btn my-4">
          <option
            v-for="segment in segments"
            :key="segment.name"
            :value="segment.name"
          >
            {{ segment.name }}
          </option>
        </select>
        <div class="text-xs text-gray-500 mb-2">
          % de pacientes que presentan malestar según
          <span v-if="segment === 'Sexo'">el sexo</span>
          <span v-if="segment === 'Edad'">la edad</span>
          <span v-if="segment === 'Ingresos'">los ingresos</span>
          <span v-if="segment === 'Economia'">la economía</span>
          en abril y diciembre
        </div>
        <arrow-plot
          :data="data1[segment]"
          :legendLabels="legendLabels"
          :xAxisDomain="[0, 80]"
        />
      </div>
    </div>

    <!-- Graph 2 (Stacked Bar Chart) -->
    <div v-if="id === 2" class="sm:shadow-md sm:rounded-lg pb-4">
      <!-- legend -->
      <div class="font-medium bg-gray-100 p-4 sm:p-6 sm:rounded-t-lg">
        Aumento de las distintas problemáticas sociales atendidas durante la
        pandemia por nuestros trabajadores y trabajadoras sociales. Puedes ver
        el porcentaje de casos en los que
        <span class="pill-5">han disminuído</span>,
        <span class="pill-4">no han aumentado nada</span>,
        <span class="pill-3">han aumentado poco</span>,
        <span class="pill-2">han aumentado bastante</span> o
        <span class="pill-1">han aumentado mucho</span>.
      </div>

      <div class="p-4 sm:p-6">
        <!-- filters -->
        <div
          class="flex flex-col sm:flex-row justify-between items-center space-y-2 mb-4"
        >
          <select v-model="area" class="btn">
            <option value="" selected>
              Principales problemáticas
            </option>
            <option v-for="area in areas" :key="area" :value="area">
              {{ area }}
            </option>
          </select>
          <ButtonSwitch
            v-model="stacked"
            :options="groupingOptions"
            class="bg-gray-100 p-1 shadow-inner text-xs rounded-full"
            item-class="text-gray-600 rounded-full hover:text-gray-700 focus:text-gray-700 transition font-medium py-2 px-4 bg-transparent focus:outline-none truncate sm:overflow-visible w-24 sm:w-auto"
            item-active-class="text-primary-700 hover:text-primary-700 focus:text-primary-800 ring-transparent ring-0"
          />
        </div>
        <!-- sort buttons -->
        <div class="h-6 items-center hidden sm:flex mt-2 mb-4 sm:mt-4">
          <div v-if="!stacked" class="grid gap-4 md:gap-8 w-full grid-cols-5">
            <button
              v-for="(val, key) in orderOptionsGraph2"
              :key="key"
              @click="setOrderGraph2(key)"
              class="btn w-full justify-center inline-flex items-center border-none p-0 text-xs font-medium shadow-none hover:shadow-none rounded-sm"
            >
              <span class="truncate">{{ val }}</span>
              <VerticalArrow
                v-if="orderByGraph2 === key"
                :dir="!orderDirGraph2"
                class="ml-2"
              />
            </button>
          </div>
        </div>
        <!-- chart -->
        <div class="relative">
          <XAxis
            :tick-values="[0, 50, 100]"
            v-if="stacked"
            class="absolute w-full h-full text-gray-500 "
          />
          <div v-else class="absolute inset-0 text-gray-500 ">
            <div class="h-full flex space-x-4 md:space-x-8">
              <XAxis
                v-for="(max, i) in axisGraph2"
                :key="i"
                :domain="[0, max]"
                :tick-values="[0, max]"
                class="w-1/5"
              />
            </div>
          </div>

          <StackedBarChart
            :data="dataGraph2"
            :color-classes="colorsGraph2"
            :stacked="stacked"
          />
        </div>
      </div>
    </div>

    <!-- Graph 3 (Arrow Plots) -->
    <div v-if="id === 3" class="sm:shadow-md sm:rounded-lg">
      <div class="font-medium bg-gray-100 p-4 sm:p-6 sm:rounded-t-lg">
        Empeoramiento de la situación económica familiar de las personas
        diagnosticadas de cáncer, en abril y diciembre, por sexo, edad e
        ingresos familiares.
      </div>
      <div class="p-4 sm:p-6 text-center">
        <div class="flex items-baseline space-x-2">
          <div class="text-gray-500 text-sm">Ver datos</div>
          <select v-model="segmentType" class="btn mb-4">
            <option
              v-for="type in segmentTypes"
              :key="type.value"
              :value="type.value"
            >
              {{ type.name }}
            </option>
          </select>
        </div>
        <!-- total -->
        <div v-if="segmentType === 'Todos'" class="w-full">
          <arrow-plot
            :data="data3.total"
            :legendLabels="legendLabels"
            :xAxisDomain="[0, 100]"
          />
        </div>
        <!-- segments -->
        <div
          v-else
          class="lg:grid lg:grid-cols-2 lg:grid-rows-2 lg:gap-3 space-y-2 lg:space-y-0"
        >
          <!-- category 1 -->
          <div class="flex flex-col sm:shadow sm:rounded-lg">
            <arrow-plot
              class="flex-grow"
              :data="[data3.total[0]]"
              :legendLabels="legendLabels"
              :xAxisDomain="[0, 100]"
            />
            <arrow-plot
              class="flex-none"
              :data="data3.segments[segmentType][data3.total[0].label]"
              :legendLabels="legendLabels"
              :xAxisDomain="[0, 100]"
            />
          </div>
          <!-- category 2 -->
          <div class="flex flex-col sm:shadow sm:rounded-lg">
            <arrow-plot
              class="flex-grow"
              :data="[data3.total[1]]"
              :legendLabels="legendLabels"
              :xAxisDomain="[0, 100]"
            />
            <arrow-plot
              class="flex-none"
              :data="data3.segments[segmentType][data3.total[1].label]"
              :legendLabels="legendLabels"
              :xAxisDomain="[0, 100]"
            />
          </div>
          <!-- category 3 -->
          <div class="flex flex-col sm:shadow sm:rounded-lg">
            <arrow-plot
              class="flex-grow"
              :data="[data3.total[2]]"
              :legendLabels="legendLabels"
              :xAxisDomain="[0, 100]"
            />
            <arrow-plot
              class="flex-none"
              :data="data3.segments[segmentType][data3.total[2].label]"
              :legendLabels="legendLabels"
              :xAxisDomain="[0, 100]"
            />
          </div>
          <!-- category 4 -->
          <div class="flex flex-col sm:shadow sm:rounded-lg">
            <arrow-plot
              class="flex-grow"
              :data="[data3.total[3]]"
              :legendLabels="legendLabels"
              :xAxisDomain="[0, 100]"
            />
            <arrow-plot
              class="flex-none"
              :data="data3.segments[segmentType][data3.total[3].label]"
              :legendLabels="legendLabels"
              :xAxisDomain="[0, 100]"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Graph 4 (Diverging Stacked Bar Chart) -->
    <div v-if="id === 4" class="sm:shadow-md sm:rounded-lg pb-4">
      <!-- legend -->
      <div class="font-medium bg-gray-100 p-4 sm:p-6 sm:rounded-t-lg">
        Escala Kessler de malestar psicológico durante la pandemia, en personas
        diagnosticadas de cáncer y familiares. Puedes ver el porcentaje de casos
        en los que cada valor ocurre
        <span class="pill-5">nunca</span>, <span class="pill-4">rara vez</span>,
        <span class="pill-nsnc">a veces</span>,
        <span class="pill-2">a menudo</span> o
        <span class="pill-1">siempre</span>.
      </div>
      <!-- chart -->
      <div class="p-4 sm:p-6">
        <div class="flex justify-end mb-4">
          <ButtonSwitch
            v-model="people1"
            :options="peopleOptions"
            class="bg-gray-100 p-1 shadow-inner text-xs rounded-full"
            item-class="text-gray-600 rounded-full hover:text-gray-700 focus:text-gray-700 transition font-medium py-2 px-4 bg-transparent focus:outline-none truncate sm:overflow-visible w-24 sm:w-auto"
            item-active-class="text-primary-700 hover:text-primary-700 focus:text-primary-800 ring-transparent ring-0"
          />
        </div>

        <div class="relative">
          <div class="absolute w-full h-full">
            <div class="w-2/3 h-full ml-auto">
              <XAxis
                :domain="[-100, 100]"
                class="w-full h-full text-gray-500"
                :highlightValue="0"
              />
            </div>
          </div>
          <DivergingStackedBars
            :data="data4[people1]"
            :colors="colorsGraph4"
            :alignLeft="true"
            :scoreDomain="[0, 4]"
            @click="people1 = $event"
          />
        </div>
      </div>
    </div>

    <!-- Graph 5 (Bar Charts) -->
    <div v-if="id === 5" class="sm:shadow-md sm:rounded-lg pb-4">
      <div class="font-medium bg-gray-100 p-4 sm:p-6 sm:rounded-t-lg">
        Principales miedos y preocupaciones durante la pandemia, en
        <span class="pill-secondary">personas diagnosticadas de cáncer</span>
        y
        <span class="pill-lightgray">población general</span>
        (según el barómetro del CIS).
      </div>
      <div class="flex flex-col w-full p-4 sm:p-6">
        <!-- sort buttons -->
        <div class="h-6 items-center hidden sm:flex mt-2 mb-4 sm:mt-4">
          <div class="grid gap-4 md:gap-8 w-full grid-cols-2">
            <button
              v-for="(val, key) in orderOptionsGraph5"
              :key="key"
              @click="setOrderGraph5(key)"
              class="btn w-full justify-center inline-flex items-center border-none p-0 text-xs font-medium shadow-none hover:shadow-none rounded-sm"
            >
              <span class="truncate">{{ val }}</span>
              <VerticalArrow
                v-if="orderByGraph5 === key"
                :dir="!orderDirGraph5"
                class="ml-2"
              />
            </button>
          </div>
        </div>
        <!-- chart -->
        <div class="relative">
          <div class="absolute inset-0 text-gray-500 ">
            <div class="h-full flex space-x-4 md:space-x-8">
              <XAxis
                v-for="(max, i) in axisGraph5"
                :key="i"
                :domain="[0, max]"
                :tick-values="[0, max]"
                class="w-1/2"
              />
            </div>
          </div>
          <MultipleBarCharts :data="dataGraph5" :color-classes="colorsGraph5" />
        </div>
        <!-- <div class="sm:flex">
          <div class="sm:w-1/2 sm:pr-4 mb-5 sm:mb-0">
            <HorizontalBarChart :data="dataGraph5" :barColor="mainBarColor" />
          </div>
          <div class="sm:w-1/2 sm:pl-4">
            <HorizontalBarChart
              :data="dataGraph5"
              :barColor="compareBarColor"
              valueProp="compare"
            />
          </div>
        </div> -->
      </div>
    </div>

    <!-- Graph 6 (Diverging Stacked Bar Chart) -->
    <div v-if="id === 6" class="sm:shadow-md sm:rounded-lg pb-4">
      <div class="font-medium bg-gray-100 p-4 sm:p-6 sm:rounded-t-lg">
        Escala ULS-6 de soledad durante la pandemia, en personas diagnosticadas
        de cáncer y familiares. Puedes ver el porcentaje de casos en los que
        cada valor ocurre
        <span class="pill-5">nunca</span>,
        <span class="pill-4">raras veces</span>,
        <span class="pill-2">algunas veces</span> o
        <span class="pill-1">frecuentemente</span>.
      </div>

      <div class="p-4 sm:p-6">
        <div class="flex justify-end mb-4">
          <ButtonSwitch
            v-model="people2"
            :options="peopleOptions"
            class="bg-gray-100 p-1 shadow-inner text-xs rounded-full"
            item-class="text-gray-600 rounded-full hover:text-gray-700 focus:text-gray-700 transition font-medium py-2 px-4 bg-transparent focus:outline-none truncate sm:overflow-visible w-24 sm:w-auto"
            item-active-class="text-primary-700 hover:text-primary-700 focus:text-primary-800 ring-transparent ring-0"
          />
        </div>

        <div class="relative">
          <div class="absolute w-full h-full">
            <div class="w-2/3 h-full ml-auto">
              <XAxis
                :domain="[-100, 100]"
                class="w-full h-full text-gray-500"
                :highlightValue="0"
              />
            </div>
          </div>
          <DivergingStackedBars
            :data="data6[people2]"
            :colors="colorsGraph6"
            :alignLeft="true"
            :scoreDomain="[1, 4]"
            @click="people2 = $event"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArrowPlot from "@/components/ArrowPlot.vue";
import ButtonSwitch from "@/components/ButtonSwitch";
// import HorizontalBarChart from "@/components/HorizontalBarChart";
import StackedBarChart from "@/components/StackedBarChart";
import DivergingStackedBars from "@/components/DivergingStackedBars";
import MultipleBarCharts from "@/components/MultipleBarCharts";
import VerticalArrow from "@/components/VerticalArrow";
import XAxis from "@/components/XAxis";
import colors from "./utils/colors.js";
import IDS from "./utils/ids.js";
import json from "@/assets/data/data";
import { max } from "d3";

export default {
  name: "App",
  components: {
    ArrowPlot,
    ButtonSwitch,
    // HorizontalBarChart,
    StackedBarChart,
    DivergingStackedBars,
    MultipleBarCharts,
    VerticalArrow,
    XAxis,
  },
  props: {
    title: {
      type: String,
      default: "",
    },
    id: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      data1: json.data1,
      data2: json.data2,
      data3: json.data3,
      data4: json.data4,
      data5: json.data5,
      data6: json.data6,
      segment: "Sexo",
      segments: [
        { name: "Sexo", value: "Sexo" },
        { name: "Edad", value: "Edad" },
        { name: "Ingresos", value: "Ingresos" },
        { name: "Economia", value: "Economia" },
      ],
      colorsGraph2: colors.graph2,
      colorsGraph3: colors.graph3,
      colorsGraph4: colors.graph4,
      colorsGraph5: colors.graph5,
      colorsGraph6: colors.graph6,
      orderByGraph5: "value",
      // orderOptions: [
      //   { name: "Pacientes", value: "value" },
      //   { name: "Población general", value: "compare" },
      // ],
      legendLabels: ["Abril", "Diciembre"],
      areas: new Set(json.data2.map((e) => e.area)),
      area: "",
      stacked: 1,
      groupingOptions: [
        { name: "Agrupado", value: 1 },
        { name: "Separado", value: 0 },
      ],
      segmentType: "Todos",
      segmentTypes: [
        { name: "Globales", value: "Todos" },
        { name: "Segmentados por sexo", value: "Sexo" },
        { name: "Segmentados por edad", value: "Edad" },
        { name: "Segmentados por ingresos", value: "Ingresos" },
      ],
      people1: "pacientes",
      people2: "pacientes",
      peopleOptions: [
        { name: "Pacientes", value: "pacientes" },
        { name: "Familiares", value: "familiares" },
      ],
      orderGraph2: {
        [IDS.HAN_DISMINUIDO]: 5,
        [IDS.NO_HAN_AUMENTADO]: 4,
        [IDS.HAN_AUMENTADO_POCO]: 3,
        [IDS.HAN_AUMENTADO_BASTANTE]: 2,
        [IDS.HAN_AUMENTADO_MUCHO]: 1,
      },
      orderByGraph2: IDS.HAN_AUMENTADO_MUCHO,
      orderDirGraph2: true,
      orderDirGraph5: true,
      orderOptionsGraph2: {
        [IDS.HAN_DISMINUIDO]: "Han disminuído",
        [IDS.NO_HAN_AUMENTADO]: "No han aumentado",
        [IDS.HAN_AUMENTADO_POCO]: "Han aumentado poco",
        [IDS.HAN_AUMENTADO_BASTANTE]: "Han aumentado bastante",
        [IDS.HAN_AUMENTADO_MUCHO]: "Han aumentado mucho",
      },
      orderOptionsGraph5: {
        value: "Pacientes",
        compare: "Población general",
      },
      orderGraph5: {
        value: 2,
        compare: 1,
      },
      width: 1000,
    };
  },
  computed: {
    dataGraph5() {
      let data = [...this.data5];

      const orderBy = this.orderByGraph5;

      return data.sort((a, b) => {
        return (
          (b.values.find((e) => e.name === orderBy).value -
            a.values.find((e) => e.name === orderBy).value) *
          (this.orderDirGraph5 ? 1 : -1)
        );
      });
    },
    dataGraph2() {
      let data = [...this.data2];
      if (this.area) data = this.data2.filter((e) => e.area === this.area);
      else {
        // sort situations by value on 'Han aumentado mucho' in descending order
        data.sort((a, b) => {
          if (a.values[4].value > b.values[4].value) return -1;
          if (a.values[4].value < b.values[4].value) return 1;
          else return 0;
        });

        data = data.slice(0, 10);
      }

      let orderBy = this.orderByGraph2;

      return data.sort((a, b) => {
        return (
          (b.values.find((e) => e.name === orderBy).value -
            a.values.find((e) => e.name === orderBy).value) *
          (this.orderDirGraph2 ? 1 : -1)
        );
      });
    },
    axisGraph2() {
      return Object.keys(this.orderGraph2).map((_, i) =>
        max(this.dataGraph2.map((e) => e.values[i].value || 0.1))
      );
    },
    axisGraph5() {
      return Object.keys(this.orderGraph5).map((_, i) =>
        max(this.dataGraph5.map((e) => e.values[i].value || 0.1))
      );
    },
  },
  methods: {
    setOrderGraph2(key) {
      if (key === this.orderByGraph2) {
        this.orderDirGraph2 = !this.orderDirGraph2;
      } else this.orderByGraph2 = key;
    },
    setOrderGraph5(key) {
      if (key === this.orderByGraph5) {
        this.orderDirGraph5 = !this.orderDirGraph5;
      } else this.orderByGraph5 = key;
    },
    setSize() {
      const container = this.$refs.container;
      this.width = container.clientWidth;
    },
    removeResizeObserver() {
      if (ResizeObserver) {
        this.resizeObserver.unobserve(this.$refs.container);
      } else {
        // for IE
        window.removeEventListener("resize", this.setSize);
      }
    },
  },
  mounted() {
    this.setSize();

    if (ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.setSize();
      });
      this.resizeObserver.observe(this.$refs.container);
    } else {
      // for IE
      window.addEventListener("resize", this.setSize);
    }
  },
  beforeDestroy() {
    this.removeResizeObserver();
  },
};
</script>
